import React, { useState } from 'react';

import 'css/components/www/LandingV3/_LandingTabs.scss';

import classnames from 'classnames';
import { type LucideIcon } from 'lucide-react';

import LazyLoadedImage from 'common/LazyLoadedImage';
import nbspLastSpace from 'common/util/nbspLastSpace';
import { type LandingImage } from 'common/www/LandingV3/types';
import { convertToSrcSetString } from 'common/www/LandingV3/utils';

type Tab = {
  icon: LucideIcon;
  iconClassModifier?: 'rotate-90' | 'rotate-neg-90';
  title: string;
  body: string;
  image: LandingImage;
  imageStyleAdjustments?: React.CSSProperties;
  tabTitle?: string;
  tabBody: (string | string[])[];
};

type Copy = {
  title: string;
  subtitle: string;
};

export type Props = {
  copy: Copy;
  tabs: Tab[];
};

const LandingTabs = ({ copy: { subtitle, title }, tabs }: Props) => {
  const [currentTab, setCurrentTab] = useState(0);

  return (
    <section className="LandingTabs">
      <h2 className="LandingTabs__title">{nbspLastSpace(title)}</h2>
      <p className="LandingTabs__subtitle">{nbspLastSpace(subtitle)}</p>

      <ul className="LandingTabs__tab-container">
        {tabs.map(({ icon: Icon, title, body, iconClassModifier }, index) => {
          return (
            <li
              className={classnames('LandingTabs__tab-item', {
                'LandingTabs__tab-item--active': currentTab === index,
              })}
              key={index}>
              <button
                className="LandingTabs__tab-button LandingTabs__tab-button--outlined"
                onClick={() => setCurrentTab(index)}>
                <Icon
                  size={24}
                  strokeWidth={2}
                  className={classnames(
                    'LandingTabs__tab-icon',
                    iconClassModifier ? `LandingTabs__tab-icon--${iconClassModifier}` : ''
                  )}
                />
                <span className="LandingTabs__tab-title">{title}</span>
                <span className="LandingTabs__tab-body">{nbspLastSpace(body)}</span>
              </button>
            </li>
          );
        })}
      </ul>
      <div className="LandingTabs__image-container">
        {tabs.map(({ title, tabTitle = title, image, tabBody, imageStyleAdjustments }, index) => {
          const isShown = index === currentTab;
          return (
            <div
              key={title}
              className={`LandingTabs__image-inner-wrapper LandingTabs__image-inner-wrapper--${
                isShown ? 'show' : 'hide'
              }`}
              aria-hidden={!isShown}>
              <div className="LandingTabs__image-copy">
                <h3 className="LandingTabs__image-title">{tabTitle}</h3>
                <div className="LandingTabs__image-body">
                  {tabBody.map((blurb) => {
                    if (typeof blurb === 'string') {
                      return (
                        <p className="LandingTabs__image-text" key={blurb}>
                          {nbspLastSpace(blurb)}
                        </p>
                      );
                    } else {
                      return (
                        <ul key={blurb[0]} className="LandingTabs__image-list">
                          {blurb.map((line) => {
                            return (
                              <li key={line} className="LandingTabs__image-line">
                                <p className="LandingTabs__image-text LandingTabs__image-text--list">
                                  {nbspLastSpace(line)}
                                </p>
                              </li>
                            );
                          })}
                        </ul>
                      );
                    }
                  })}
                </div>
              </div>
              <LazyLoadedImage
                {...image}
                srcSet={convertToSrcSetString(image.srcSet)}
                className="LandingTabs__image-img"
                loading="lazy"
                style={imageStyleAdjustments}
              />
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default LandingTabs;
